import { useState } from 'react';
import { TextField } from '@mui/material';
import { jordanDate } from '@serb/shared';
import { FormField } from '../../components/FormField';
import { FormDialog, FullRow } from '../../components/FormDialog';
import { useNotice } from '../../components/NoticeProvider';
import { useShipments } from '../shipments/useShipments';
import { DOC_SOURCES, DOC_STATUSES, DOC_TYPES, finderPath, type DocRecord, type DocStatus } from './sample';

type Props = { open: boolean; onClose: () => void; onAdd: (r: DocRecord) => void };

const blank = () => ({
  name: '',
  srb: '',
  type: 'Other',
  source: 'Customer',
  status: 'Received' as DocStatus,
  date: jordanDate(),
  path: '',
  notes: '',
});

/** A form to record a document. It records only: nothing is created, copied or moved on the Mac. */
export function AddDocumentDialog({ open, onClose, onAdd }: Props) {
  const notice = useNotice();
  const { items: shipments } = useShipments();
  const [form, setForm] = useState(blank);
  const set = (patch: Partial<ReturnType<typeof blank>>) => setForm((f) => ({ ...f, ...patch }));
  const save = () => {
    const dot = form.name.lastIndexOf('.');
    const ext = dot > 0 && form.name.length - dot <= 5 ? form.name.slice(dot + 1).toLowerCase() : '';
    const name = ext ? form.name.slice(0, dot) : form.name.trim();
    onAdd({
      id: `d${Date.now()}`,
      name,
      ext,
      srb: form.srb,
      type: form.type,
      source: form.source,
      status: form.status,
      date: form.date,
      path: form.path || (form.status === 'Missing' ? '' : finderPath(form.srb, { name, ext })),
      notes: form.notes,
    });
    setForm(blank());
    onClose();
    notice('Record added for now. Nothing was created or moved on your Mac.', 'success');
  };
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Add a document record"
      saveLabel="Add record"
      canSave={Boolean(form.name.trim())}
      onSave={save}
    >
      <FullRow>
        <TextField
          label="Document name"
          required
          placeholder="e.g. Commercial Invoice.pdf"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
        />
      </FullRow>
      <FormField
        label="Shipment (SRB code)"
        value={form.srb}
        onChange={(srb) => set({ srb })}
        options={shipments.map((s) => s.srb)}
      />
      <FormField label="Type" value={form.type} onChange={(type) => set({ type })} options={DOC_TYPES} />
      <FormField label="Source" value={form.source} onChange={(source) => set({ source })} options={DOC_SOURCES} />
      <FormField
        label="Status"
        value={form.status}
        onChange={(status) => set({ status: status as DocStatus })}
        options={DOC_STATUSES}
      />
      <FormField label="Date" type="date" value={form.date} onChange={(date) => set({ date })} />
      <TextField
        label="File in Finder (optional)"
        placeholder="SERB Express/Shipments/…"
        value={form.path}
        onChange={(e) => set({ path: e.target.value })}
      />
      <FullRow>
        <TextField
          label="Notes (optional)"
          multiline
          minRows={2}
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
        />
      </FullRow>
    </FormDialog>
  );
}
